/**
 * Cache de requisições em memória (apenas durante a sessão do navegador).
 *
 * NADA é persistido em disco. É uma otimização de sessão: voltar da ficha para a
 * lista, repaginar 1→2→1 ou repetir um lookup evita ir à rede. TTL curto (10min)
 * é só higiene de memória — os dados da RFB mudam ~1x/mês. Erros NUNCA são
 * cacheados. Requisições idênticas concorrentes compartilham a mesma Promise
 * (dedupe de requisições em voo).
 */

interface CacheEntry {
  value: unknown;
  /** Timestamp de expiração; `Infinity` = TTL de sessão (não expira). */
  expiresAt: number;
}

/** TTL de 6 horas (busca/ficha/sócios). O LRU (50 entradas) controla a memória. */
export const TTL_6_HOURS = 6 * 60 * 60 * 1000;
/** TTL de sessão: não expira (lookups estáticos no mês). */
export const TTL_SESSION = Number.POSITIVE_INFINITY;

const MAX_ENTRIES = 50;
const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<unknown>>();

/** Serialização estável (chaves ordenadas) — mesma entrada gera a mesma chave. */
function stableStringify(value: unknown): string {
  if (value === undefined) {
    return "null";
  }
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }
  const obj = value as Record<string, unknown>;
  const entries = Object.keys(obj)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableStringify(obj[key])}`);
  return `{${entries.join(",")}}`;
}

/** Chave canônica de cache a partir do endpoint + payload. */
export function cacheKey(endpoint: string, payload: unknown): string {
  return `${endpoint}|${stableStringify(payload)}`;
}

/**
 * Busca com cache LRU + dedupe de requisições em voo. Só resultados de sucesso
 * são cacheados (erros propagam e nunca ficam guardados).
 */
export function cachedFetch<T>(key: string, ttlMs: number, fetcher: () => Promise<T>): Promise<T> {
  const now = Date.now();

  const entry = cache.get(key);
  if (entry) {
    if (entry.expiresAt > now) {
      // Hit válido: renova a posição LRU (mais recente ao final do Map).
      cache.delete(key);
      cache.set(key, entry);
      return Promise.resolve(entry.value as T);
    }
    cache.delete(key); // expirado
  }

  const pending = inflight.get(key) as Promise<T> | undefined;
  if (pending) {
    return pending; // dedupe: compartilha a requisição já em voo
  }

  const request = fetcher()
    .then((value) => {
      cache.set(key, { value, expiresAt: Date.now() + ttlMs });
      if (cache.size > MAX_ENTRIES) {
        const oldest = cache.keys().next().value;
        if (oldest !== undefined) {
          cache.delete(oldest);
        }
      }
      return value;
    })
    .finally(() => {
      inflight.delete(key);
    });

  inflight.set(key, request);
  return request;
}

/** Limpa todo o cache e as requisições em voo (ex.: no logout). */
export function clearAll(): void {
  cache.clear();
  inflight.clear();
}
